import React, { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";

// --- Générateur de fluctuation pseudo-chaotique ---
function chaoticDrift(t, entropy) {
  const base = Math.sin(t * 1.37) * Math.cos(t * 0.73);
  const phase = Math.sin(t * 0.91 + entropy * 0.27);
  const noise = (base + phase + Math.random() * 0.3) * 0.5;
  return noise;
}

// --- Noyau énergétique (planète centrale) ---
function EnergyCore({ glow, time, entropy }) {
  const meshRef = useRef();

  useFrame(() => {
    if (!meshRef.current) return;

    // Variation fluide + composante chaotique
    const noise = chaoticDrift(time.current * 0.8, entropy.accumulator);
    const scale =
      1.2 + Math.sin(time.current * 3) * 0.1 + entropy.shannon * 0.02 + noise * 0.1;

    meshRef.current.scale.set(scale, scale, scale);

    // Couleur : dérive lente + impulsion stochastique
    const hue =
      (time.current * 0.05 + entropy.shannon * 0.01 + noise * 0.3) % 1;
    meshRef.current.material.color.setHSL(hue, 1, 0.5);
    meshRef.current.material.emissive.setHSL(
      (hue + 0.25 + noise * 0.1) % 1,
      1,
      0.55
    );
    meshRef.current.material.emissiveIntensity = glow
      ? 1.4 + Math.abs(noise) * 1.2
      : 0.2;
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[6, 128, 128]} />
      <meshStandardMaterial
        metalness={0.25}
        roughness={0.35}
        transparent
        opacity={0.95}
      />
    </mesh>
  );
}

// --- Anneaux orbitaux ---
function AliceRing({ name, radius, color, eclipticFactor = 1, entropy }) {
  const ringRef = useRef();

  useFrame((_, delta) => {
    if (!ringRef.current) return;
    const noise = chaoticDrift(entropy.accumulator * 0.05, entropy.shannon);
    const boost = 1 + Math.abs(noise) * 0.6;

    ringRef.current.rotation.x += delta * 0.3 * eclipticFactor * boost;
    ringRef.current.rotation.y += delta * 0.2 * eclipticFactor * boost;
    ringRef.current.rotation.z += delta * 0.15 * eclipticFactor * boost;
  });

  return (
    <mesh ref={ringRef} name={name}>
      <torusGeometry args={[radius, 0.25, 32, 200]} />
      <meshBasicMaterial color={color} transparent opacity={0.6} />
    </mesh>
  );
}

// --- Particules stellaires ---
function EnergyParticles({ entropy }) {
  const pointsRef = useRef();
  const particleCount = 1000;
  const positions = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    const r = 20 + Math.random() * 10;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }

  useFrame(({ clock }) => {
    if (!pointsRef.current) return;
    const t = clock.elapsedTime;
    const noise = chaoticDrift(t * 0.7, entropy.shannon);

    pointsRef.current.rotation.y = t * (0.1 + noise * 0.05);
    pointsRef.current.material.opacity =
      0.5 + Math.sin(t * 0.8) * 0.15 + Math.abs(noise) * 0.2;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial color="#00ffff" size={0.3} transparent opacity={0.7} />
    </points>
  );
}

// --- Noyau principal (planète + anneaux + particules) ---
function MetaCubeOracleV3Core({
  running = true,
  chaos = false,
  glow = true,
  eclipticSpeedPct = 50,
  onEntropyUpdate = () => {},
}) {
  const groupRef = useRef();
  const time = useRef(0);
  const [entropy, setEntropy] = useState({ shannon: 0, accumulator: 0 });

  // --- Récupération d’entropie dynamique (non déterministe) ---
  useEffect(() => {
    let active = true;
    const fetchEntropy = async () => {
      try {
        // ⚠️ LIT LA MESURE, PLUS UN CHAMP ALÉATOIRE DE /animate.
        //
        // Le canal dynamique affiché venait de `/animate`, dont les champs
        // `shannon_entropy` et `entropy_accumulator` sont des tirages — et le
        // backend a cessé d'en écrire la métrique le 2026-07-29. La page
        // montrait donc un nombre qui n'était mesuré nulle part.
        //
        // Elle lit désormais `/dyn_entropy`, qui mesure la scène canonique.
        //
        // Le front multipliait en plus la valeur reçue par son PROPRE
        // `Math.random()` — une pseudo-mesure posée sur une autre.
        const res = await fetch("/api/geometry/metacube_oracle_v3/dyn_entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        });
        const data = await res.json();
        if (active && typeof data.entropy_dyn === "number") {
          const val = {
            shannon: data.entropy_dyn || 0,
            accumulator: data.entropy_dyn || 0,
          };
          setEntropy(val);
          onEntropyUpdate(val);
        }
      } catch (err) {
        console.warn("Erreur backend MetaCubeOracleV3:", err);
      }
    };

    if (running) {
      fetchEntropy();
      const id = setInterval(fetchEntropy, 1200 + Math.random() * 800); // ⟵ jitter aléatoire
      return () => {
        active = false;
        clearInterval(id);
      };
    }
  }, [running, onEntropyUpdate]);

  // --- Animation globale ---
  useFrame((_, delta) => {
    if (!running || !groupRef.current) return;
    time.current += delta;

    const noise = chaoticDrift(time.current * 0.5, entropy.shannon);

    if (chaos) {
      groupRef.current.position.x = Math.sin(time.current * 2.7) * 0.5 + noise;
      groupRef.current.position.y = Math.cos(time.current * 1.9) * 0.4 + noise * 0.6;
    } else {
      groupRef.current.position.set(0, 0, 0);
    }
  });

  const eclipticFactor = eclipticSpeedPct / 50;

  return (
    <group ref={groupRef}>
      <EnergyCore glow={glow} time={time} entropy={entropy} />

      {/* Anneaux d’Alice en 3 trios */}
      {[0, 1, 2].map((i) => (
        <AliceRing
          key={`ringA-${i}`}
          name={`ringA-${i}`}
          radius={12 + i * 3}
          color={new THREE.Color().setHSL(i / 3, 1, 0.6)}
          eclipticFactor={eclipticFactor}
          entropy={entropy}
        />
      ))}
      {[0, 1, 2].map((i) => (
        <AliceRing
          key={`ringB-${i}`}
          name={`ringB-${i}`}
          radius={12 + i * 3}
          color={new THREE.Color().setHSL((i + 1) / 3, 1, 0.6)}
          eclipticFactor={eclipticFactor}
          entropy={entropy}
        />
      ))}
      {[0, 1, 2].map((i) => (
        <AliceRing
          key={`ringC-${i}`}
          name={`ringC-${i}`}
          radius={12 + i * 3}
          color={new THREE.Color().setHSL((i + 2) / 3, 1, 0.6)}
          eclipticFactor={eclipticFactor}
          entropy={entropy}
        />
      ))}

      <EnergyParticles entropy={entropy} />
    </group>
  );
}

export default function MetaCubeOracleV3(props) {
  return (
    <div className="metacube-v3-canvas">
      <Canvas camera={{ position: [0, 20, 45], fov: 65 }}>
        <ambientLight intensity={0.4} />
        <pointLight position={[30, 30, 30]} intensity={1.5} color="#00ffff" />
        <MetaCubeOracleV3Core {...props} />
        <OrbitControls enableZoom enablePan />
        <EffectComposer>
          <Bloom intensity={2.5} radius={1.2} threshold={0.0} />
        </EffectComposer>
      </Canvas>
    </div>
  );
}
